/* eslint react-hooks/exhaustive-deps: "warn" */

import React, { useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MaActivityCompareSection.module.scss';

type Translate = (key: string) => string;

const getRows = (t: Translate) => [
  {
    num: '01',
    label: t('Coverage'),
    gdbTitle: t('600M+ registered companies'),
    gdbDesc: t('Every company — funded or not'),
    mkt: t('PitchBook ~8M · Crunchbase ~24M — mainly funded companies'),
  },
  {
    num: '02',
    label: t('Financials'),
    gdbTitle: t('Filed financial statements'),
    gdbDesc: t('For private companies across 200+ countries, from government registries'),
    mkt: t('Mainly companies that received funding'),
  },
  {
    num: '03',
    label: t('Financial filings'),
    gdbTitle: t('Original filings included'),
    gdbDesc: t('Download the source document behind every statement'),
    mkt: '\u2014',
  },
  {
    num: '04',
    label: t('New incorporations'),
    gdbTitle: t('Daily'),
    gdbDesc: t('Direct from registry feeds'),
    mkt: '\u2014',
  },
  {
    num: '05',
    label: t('Provenance'),
    gdbTitle: t('Source + timestamp on every attribute'),
    gdbDesc: t('Registry name, filing link, retrieval time'),
    mkt: '\u2014',
  },
  {
    num: '06',
    label: t('Licensing'),
    gdbTitle: t('Flexible reseller rights'),
    gdbDesc: t('Redistribution & AI training available'),
    mkt: t('Restricted use'),
  },
];

export const MaActivityCompareSection = () => {
  const { t } = useInt();
  const wrapRef = useRef<HTMLDivElement>(null);
  const inView = useIntersection(wrapRef, { threshold: 0.05 });
  const rows = getRows(t);

  return (
    <div ref={wrapRef}>
      <Section
        background="radial-gradient(ellipse 55% 55% at 85% 0%, rgba(51, 92, 244, 0.35), transparent 70%), radial-gradient(ellipse 45% 45% at 5% 100%, rgba(1, 83, 244, 0.25), transparent 70%), radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.06) 1px, transparent 0) 0 0 / 28px 28px, linear-gradient(160deg, #081d3d 0%, #0e1526 45%, #0c257f 130%)"
        className={styles.section}
      >
        <div className={cn(styles.head, inView && styles.headVisible)}>
          <TitleBlock
            theme="dark"
            title={t('How Global Database compares')}
            description={t(
              'PitchBook and Crunchbase cover funded deals well. The difference is everything around them.',
            )}
          />
        </div>

        <div className={styles.table}>
          <div className={cn(styles.tableHead, inView && styles.tableHeadVisible)}>
            <span />
            <span className={styles.colGdb}>
              <Icon type="logo-1" className={styles.logo} />
            </span>
            <span className={styles.colMkt}>PitchBook &middot; Crunchbase</span>
          </div>

          {rows.map((row, i) => (
            <div
              key={row.num}
              className={cn(styles.row, inView && styles.rowVisible)}
              style={{ '--delay': `${i * 0.08}s` } as React.CSSProperties}
            >
              <div className={styles.cap}>
                <span className={styles.num}>{row.num}</span>
                <strong className={styles.capLabel}>{row.label}</strong>
              </div>
              <div className={styles.gdb}>
                <strong className={styles.gdbTitle}>{row.gdbTitle}</strong>
                <span className={styles.gdbDesc}>{row.gdbDesc}</span>
              </div>
              <div className={styles.mkt}>{row.mkt}</div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};
